// Company settings — Supabase implementation.
//
// SECURITY NOTE, and the reason this file looks different from its Firestore
// ancestor: gateway credentials are no longer readable here at all.
//
// company_info is staff-readable, exactly as the Firestore document was. The
// difference is what it now contains. Access tokens, API keys and HMAC secrets
// live in `integration_secrets`, a table with RLS on and no policies, so only
// the service role (edge functions) can read them. Previously every cashier's
// browser downloaded the WhatsApp, Hermes and OpenClaw keys along with the
// company address.
//
// Consequence: these forms write non-secret settings only. Secrets go through
// the owner-only `admin-secrets` edge function below, and the UI shows whether
// one is present, never its value — a set is write-only, there is no read-back.

import { supabase } from '@/lib/supabase';
import { tsReq } from './mappers';
import type { Database } from '@/types/database';
import type {
  CompanyInfo,
  HermesConfig,
  N8nConfig,
  OpenClawConfig,
  PaymentMethod,
  WhatsAppCloudConfig,
} from '@/types/domain';

type Row = Database['public']['Tables']['company_info']['Row'];

function mapCompany(r: Row): CompanyInfo {
  return {
    id: 'singleton',
    name: r.name,
    address: r.address ?? undefined,
    phone: r.phone ?? undefined,
    email: r.email ?? undefined,
    logoUrl: r.logo_url ?? undefined,
    npwp: r.npwp ?? undefined,
    customDomain: r.custom_domain ?? undefined,
    defaultTaxRate: Number(r.default_tax_rate),
    defaultTaxEnabled: r.default_tax_enabled,
    invoiceCounter: 0, // per-event now; kept for type compatibility
    paymentMethods: (r.payment_methods ?? []) as PaymentMethod[],
    whatsappCloud: r.whatsapp_cloud as unknown as WhatsAppCloudConfig,
    n8n: r.n8n as unknown as N8nConfig,
    hermes: r.hermes as unknown as HermesConfig,
    openclaw: r.openclaw as unknown as OpenClawConfig,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

export async function getCompanyInfo(): Promise<CompanyInfo> {
  const { data, error } = await supabase
    .from('company_info').select('*').eq('id', true).single();
  if (error) throw new Error(error.message);
  return mapCompany(data);
}

export async function updateCompanyInfo(
  input: Partial<Omit<CompanyInfo, 'id' | 'createdAt' | 'updatedAt'>>,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name;
  if (input.address !== undefined) patch.address = input.address || null;
  if (input.phone !== undefined) patch.phone = input.phone || null;
  if (input.email !== undefined) patch.email = input.email || null;
  if (input.logoUrl !== undefined) patch.logo_url = input.logoUrl || null;
  if (input.npwp !== undefined) patch.npwp = input.npwp || null;
  if (input.customDomain !== undefined) patch.custom_domain = input.customDomain || null;
  if (input.defaultTaxRate !== undefined) patch.default_tax_rate = input.defaultTaxRate;
  if (input.defaultTaxEnabled !== undefined) patch.default_tax_enabled = input.defaultTaxEnabled;
  const { error } = await supabase.from('company_info').update(patch as never).eq('id', true);
  if (error) throw new Error(error.message);
}

export async function updatePaymentMethods(methods: PaymentMethod[]): Promise<void> {
  const { error } = await supabase
    .from('company_info').update({ payment_methods: methods }).eq('id', true);
  if (error) throw new Error(error.message);
}

/** Merge-update one gateway's non-secret settings. */
async function updateGateway(
  column: 'whatsapp_cloud' | 'n8n' | 'hermes' | 'openclaw',
  patch: Record<string, unknown>,
): Promise<void> {
  const current = await getCompanyInfo();
  const key = (
    { whatsapp_cloud: 'whatsappCloud', n8n: 'n8n', hermes: 'hermes', openclaw: 'openclaw' } as const
  )[column];
  const merged = { ...(current[key] ?? {}), ...patch };
  const { error } = await supabase
    .from('company_info').update({ [column]: merged } as never).eq('id', true);
  if (error) throw new Error(error.message);
}

export const updateWhatsAppCloudConfig = (i: Partial<WhatsAppCloudConfig>) =>
  updateGateway('whatsapp_cloud', i);
export const updateN8nConfig = (i: Partial<N8nConfig>) => updateGateway('n8n', i);
export const updateHermesConfig = (i: Partial<HermesConfig>) => updateGateway('hermes', i);
export const updateOpenClawConfig = (i: Partial<OpenClawConfig>) => updateGateway('openclaw', i);

/**
 * Which integration secrets are configured — names only, never values.
 * Backed by an edge function running as the service role, because
 * integration_secrets is deliberately unreadable from the browser.
 */
export async function listConfiguredSecrets(): Promise<string[]> {
  const { data, error } = await supabase.functions.invoke<{ keys: string[] }>(
    'admin-secrets',
    { body: { action: 'list' } },
  );
  if (error) return [];
  return data?.keys ?? [];
}

/** Keys `admin-secrets` accepts. Kept in sync with ALLOWED_KEYS there. */
export type SecretKey =
  | 'WHATSAPP_ACCESS_TOKEN'
  | 'HERMES_API_KEY'
  | 'HERMES_HMAC_SECRET'
  | 'OPENCLAW_API_KEY'
  | 'N8N_HMAC_SECRET';

/**
 * Store a credential. Owner-only, enforced in the edge function — the browser
 * never touches integration_secrets directly.
 *
 * Write-only by design: there is no getSecret(). Once set, the value is
 * readable solely by the service role, so a mistyped token is replaced rather
 * than inspected.
 *
 * `supabase.functions.invoke` resolves with `error` set on a non-2xx, but the
 * body carries the actual reason ("Value tidak boleh kosong", "Unknown secret
 * key: …"). Surfacing that instead of the generic transport message is the
 * difference between a fixable message and "Edge Function returned a non-2xx
 * status code".
 */
export async function setSecret(key: SecretKey, value: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
    'admin-secrets',
    { body: { action: 'set', key, value } },
  );
  if (data?.error) throw new Error(data.error);
  if (error) throw new Error(error.message);
}

/** Remove a credential. Disables whichever gateway depended on it. */
export async function unsetSecret(key: SecretKey): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
    'admin-secrets',
    { body: { action: 'unset', key } },
  );
  if (data?.error) throw new Error(data.error);
  if (error) throw new Error(error.message);
}
